import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Render-error boundary. Without one, any uncaught render error unmounts the
 * whole React tree and leaves a silent blank page (the 2026-07-08 prod
 * incident's failure mode). Class component — the only way to catch render
 * errors in React.
 *
 * By default renders the full-screen {@link CrashFallback} (app-root usage in
 * App.tsx). Pass `fallback` to scope the blast radius to a panel — e.g. the
 * assistant-ui thread — so the rest of the app stays interactive.
 */
export class ErrorBoundary extends Component<
  {
    children: ReactNode;
    fallback?: (reset: () => void, error: Error) => ReactNode;
  },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Crash forensics — component stack tells us which surface blew up.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.reset, this.state.error);
    return (
      <CrashFallback
        error={this.state.error}
        onReload={() => window.location.reload()}
        onReset={this.reset}
      />
    );
  }
}

function CrashFallback({
  error,
  onReload,
  onReset,
}: {
  error: Error;
  onReload: () => void;
  onReset: () => void;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
      <div className="w-full max-w-lg rounded-lg border border-border bg-card p-8 text-center shadow-sm">
        <h1 className="text-lg font-semibold">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          The app hit an unexpected error. Reloading usually fixes it — nothing
          you were working on server-side is lost.
        </p>
        <pre className="mt-4 max-h-32 overflow-auto rounded-md bg-muted p-3 text-left text-xs text-muted-foreground">
          {error.message}
        </pre>
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={onReload}
            className="focus-ring rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500"
          >
            Reload
          </button>
          <button
            type="button"
            onClick={onReset}
            className="focus-ring rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}
